import { Box, withStyles, type Timestamp, type Timezone, type TransactionHistoryStatus } from 'vopay';
import { useTranslation } from 'react-i18next';
import { Button } from '#shared/components/UI';
import { Balance } from '#shared/hooks/useApi/useAccountBalances';
import styles from './Transactions.module.scss';
import useFilterFormConfig, { TransactionSearchParams } from './useFilterFormConfig';

const cn = withStyles(styles);

// Exported surface (ID-13367): the client portal's Transaction History page and the Customer Activity tab
// both mount this filter bar, so it takes everything as props and reads no feature context. Owns only the
// two-row grid; useFilterFormConfig owns the form state and field vocab.
type SearchFormProps = {
  type: string;
  status: TransactionHistoryStatus;
  flagged: string;
  currency: string;
  searchTerm: string;
  timeZone: Timezone;
  balances: Balance[];
  country: string;
  subAccountPermissions: boolean;
  canViewSubaccounts: boolean;
  initialTimeRange: [Timestamp, Timestamp];
  hasInvalidEndDate: boolean;
  shouldShowEndDateError: boolean;
  includeSubAccounts: boolean;
  settlementDate: Timestamp | undefined;
  setShouldShowEndDateError: (event: boolean) => void;
  onSearch: (searchParams: TransactionSearchParams) => void;
};

export default function TransactionSearchForm({
  type,
  status,
  flagged,
  currency,
  searchTerm,
  timeZone,
  balances,
  country,
  subAccountPermissions,
  canViewSubaccounts,
  initialTimeRange,
  hasInvalidEndDate,
  shouldShowEndDateError,
  includeSubAccounts,
  settlementDate,
  setShouldShowEndDateError,
  onSearch,
}: SearchFormProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.TransactionHistory.TransactionSearchForm' });

  const { ctx, renderField } = useFilterFormConfig({
    type,
    status,
    flagged,
    currency,
    searchTerm,
    timeZone,
    settlementDate,
    includeSubAccounts,
    initialTimeRange,
    balances,
    country,
    subAccountPermissions,
    canViewSubaccounts,
    hasInvalidEndDate,
    shouldShowEndDateError,
    setShouldShowEndDateError,
    onSearch,
  });

  return (
    <form onSubmit={ctx.handleSubmit}>
      <Box flx="col" gapSize="16px">
        <div className={cn('firstRow')}>
          {renderField('timeRange')}
          <div className={cn('timezone')}>{renderField('timeZone')}</div>
        </div>

        <div className={cn('secondRow')}>
          <div className={cn('defaultWidth')}>{renderField('currency')}</div>
          <div className={cn('defaultWidth', 'type')}>{renderField('type')}</div>
          <div className={cn('defaultWidth', 'status')}>{renderField('status')}</div>
          <div className={cn('defaultWidth')}>{renderField('flagged')}</div>
          <div className={cn('defaultWidth', 'settlementDate')}>{renderField('settlementDate')}</div>
          <div className={cn('defaultWidth')}>{renderField('searchTerm')}</div>

          <Button id="search-transaction-history" size="small" colorScheme="primary" type="submit">
            {t('search')}
          </Button>
        </div>

        {renderField('includeSubAccounts')}
      </Box>
    </form>
  );
}
